import concat from 'lodash/concat';
import compact from 'lodash/compact';
import { PATIENT_TYPE_KIDS } from '../../../../../constants';
import {
  isClosedTodayAndTomorrow,
  isPediatricUrgentCare,
  isAdultPatientsAccepted,
  isPediatricPatientsAccepted,
  hasAvailabilityTodayOrTomorrow,
} from '../../../../../core/util/location';
import { isEmptyArray } from '../../../../../core/util/array';
import {
  getLocationsSlotsAfterRequestedAppointmentTime,
  hasSlotsAvailable,
} from '../../../../../core/location/slots';
import moment from 'moment-timezone';
import { call } from 'redux-saga/effects';
import { apiGet } from '../../../../../core/dapi';
import { getTelemedLocationUrl } from '../../../../../core/dapi/location';
import { locationResponseFormatter } from '../../../../../reducers/formatters/location';
import { getRandomInt } from '../../../../../core/util/numeric';
import { DENTIST_VALUE, URGENT_CARE_VALUE } from '../../../../Home/Tiles/ProviderTypes';
import { isRequestedInsuranceMatchingLocation } from '../../../../../core/util/insurance';
import { isEmptyObject } from '../../../../../core/util/object';
import { getMilesFromMeters } from '../../../../../core/util/geo';

const REQUESTED_APPOINTMENT_TIME_BUFFER = 30; // minutes
export const BEST_MATCH_CUTOFF_IN_MILES = 15; // SOLV-5684: update best match cutoff in miles from 6 miles to 15 miles

const distanceComparator = (locationA, locationB) =>
  locationA.distanceFromCurrentLocation - locationB.distanceFromCurrentLocation;

const requestedInsuranceAcceptedComparator = requestedInsurance => (locationA, locationB) => {
  if (
    isRequestedInsuranceMatchingLocation(requestedInsurance, locationA) &&
    !isRequestedInsuranceMatchingLocation(requestedInsurance, locationB)
  ) {
    return -1;
  } else if (
    isRequestedInsuranceMatchingLocation(requestedInsurance, locationB) &&
    !isRequestedInsuranceMatchingLocation(requestedInsurance, locationA)
  ) {
    return 1;
  }

  return 0;
};

const getUpdatedLocationWithSlotsAfterTime = (location, requestedAppointmentTime) => {
  const updatedRequestedAppointmentTime = moment(requestedAppointmentTime)
    .tz(location.timeZone)
    .subtract(REQUESTED_APPOINTMENT_TIME_BUFFER, 'minutes')
    .valueOf();
  const slotsAfter = getLocationsSlotsAfterRequestedAppointmentTime(
    location,
    updatedRequestedAppointmentTime
  );
  return { ...location, slots: slotsAfter };
};

export const getUpdatedLocationsWithSlotsAfterTime = (locations, requestedAppointmentTime) =>
  locations.map(location =>
    getUpdatedLocationWithSlotsAfterTime(location, requestedAppointmentTime)
  );

/**
 * Get the best match for a given location list.
 * @param {Array} locations
 * @param {Object} searchFilters
 * @returns {IterableIterator<Array>}
 */
export function* getBestMatchForLocations(locations, searchFilters) {
  const { patientType: rawPatientType, providerType, requestedInsurance } = searchFilters;
  const patientType = typeof rawPatientType === 'object' ? rawPatientType.value : rawPatientType;
  const bestMatch = [];

  // only locations with appointment availability are eligible for best match
  let filteredLocations = locations.filter(location => hasSlotsAvailable(location.slots));

  // only locations < BEST_MATCH_CUTOFF_IN_MILES miles are eligible for best match
  filteredLocations = filteredLocations.filter(
    location =>
      getMilesFromMeters(location.distanceFromCurrentLocation) <= BEST_MATCH_CUTOFF_IN_MILES
  );

  // 2019-04-09
  // if you are searching for a dentist and specified your requested search insurance
  // the best match should be the closest location with availability that accepts that insurance
  if (providerType === DENTIST_VALUE && !isEmptyObject(requestedInsurance)) {
    const locationsMatchingRequestedInsurance = filteredLocations.filter(location =>
      isRequestedInsuranceMatchingLocation(requestedInsurance, location)
    );

    if (!isEmptyArray(locationsMatchingRequestedInsurance)) {
      filteredLocations = locationsMatchingRequestedInsurance.sort(distanceComparator);
    }
  }

  if (patientType === PATIENT_TYPE_KIDS) {
    // 2019-01-09
    // Best match for "kids" searches is defined as:
    // if results set includes:
    // a pediatric clinic, the closest one is the best match
    // a clinic that accepts kids but not adults, that's the best match
    // a clinic that accepts both kids && adults [basically the closest (by distance) clinic]
    for (let location of filteredLocations) {
      if (
        isPediatricUrgentCare(location) ||
        (isPediatricPatientsAccepted(location) && !isAdultPatientsAccepted(location))
      ) {
        bestMatch.push({ ...location, isBestMatch: true });
        break;
      }
    }
  } else {
    // 2019-01-03
    // Best match for "adults" searches is defined as:
    // the closest (by distance) clinic
    if (filteredLocations[0]) bestMatch.push({ ...filteredLocations[0], isBestMatch: true });
  }

  // 2019-04-15
  // Telemed in the best match should appear only if provider type searched is urgent care
  if (providerType === URGENT_CARE_VALUE) {
    try {
      const groupIds = bestMatch[0].groups.map(group => group.group_id);

      const url = getTelemedLocationUrl({ groupIds, patientType });
      const telemedResponse = yield call(apiGet, url);

      const telemedBestMatch =
        telemedResponse.results[getRandomInt(telemedResponse.results.length)];

      if (telemedBestMatch) bestMatch.push(locationResponseFormatter(telemedBestMatch));
    } catch (e) {
      console.warn("Can't get telemed best match", e);
    }
  }

  return bestMatch;
}

export const getLocationsWithoutBestMatch = (locations, bestMatch) => {
  if (isEmptyArray(locations)) {
    return [];
  }

  const bestMatchIds = bestMatch.map(location => location.id);
  return locations.filter(location => !bestMatchIds.includes(location.id));
};

const availabilityComparator = newBooking => (locationA, locationB) => {
  // 2019-01-09
  // Main partner results set should be ordered as follows
  // if a is closed and has no avail and b is open with avail, sort b on top of a
  if (
    isClosedTodayAndTomorrow(locationA) &&
    !hasAvailabilityTodayOrTomorrow(locationA, newBooking) &&
    (!isClosedTodayAndTomorrow(locationB) && hasAvailabilityTodayOrTomorrow(locationB, newBooking))
  ) {
    return 1;
  }
  // if a is open but no avail and b is open with avail, sort b on top of a
  if (
    !isClosedTodayAndTomorrow(locationA) &&
    !hasAvailabilityTodayOrTomorrow(locationA, newBooking) &&
    (!isClosedTodayAndTomorrow(locationB) && hasAvailabilityTodayOrTomorrow(locationB, newBooking))
  ) {
    return 1;
  }
  // if a is open with avail and b is closed and without avail return a
  if (
    !isClosedTodayAndTomorrow(locationA) &&
    hasAvailabilityTodayOrTomorrow(locationA, newBooking) &&
    (isClosedTodayAndTomorrow(locationB) && !hasAvailabilityTodayOrTomorrow(locationB, newBooking))
  ) {
    return -1;
  }
  // if a is open with avail and b is closed and without avail return a
  if (
    !isClosedTodayAndTomorrow(locationA) &&
    hasAvailabilityTodayOrTomorrow(locationA, newBooking) &&
    (!isClosedTodayAndTomorrow(locationB) && !hasAvailabilityTodayOrTomorrow(locationB, newBooking))
  ) {
    return -1;
  }
  // if a is open with avail and b is open with avail don't sort
  return 0;
};

/**
 * Combine the searchResults obj into a concatenated Array.
 * @param {{ bestMatchResults: {Array},
 *           nonPartnersResults: Array,
 *           partnersResults: Array }} searchResults
 * @returns {Array}
 */
export const combineSearchResults = searchResults =>
  compact(
    concat(
      searchResults.partnersResults,
      searchResults.nonPartnersResults,
      searchResults.bestMatchResults
    )
  );

const primaryAvailabilitySecondaryDistanceComparator = (locationA, locationB, newBooking) => {
  let comparatorValue = availabilityComparator(newBooking)(locationA, locationB);
  if (comparatorValue !== 0) {
    return comparatorValue;
  }
  comparatorValue = distanceComparator(locationA, locationB);
  return comparatorValue;
};

export const sortPartnerResults = (newBooking, locations) =>
  locations.sort((a, b) => primaryAvailabilitySecondaryDistanceComparator(a, b, newBooking));

export const primaryRequestedInsuranceAcceptedSecondaryAvailabilityTertiaryDistanceComparator = (
  locationA,
  locationB,
  requestedInsurance,
  newBooking
) => {
  let comparatorValue = requestedInsuranceAcceptedComparator(requestedInsurance)(
    locationA,
    locationB
  );
  if (comparatorValue !== 0) {
    return comparatorValue;
  }

  comparatorValue = availabilityComparator(newBooking)(locationA, locationB);
  if (comparatorValue !== 0) {
    return comparatorValue;
  }
  comparatorValue = distanceComparator(locationA, locationB);
  return comparatorValue;
};

export const sortNonUrgentCarePartnerResults = (newBooking, requestedInsurance, locations) =>
  locations.sort((a, b) =>
    primaryRequestedInsuranceAcceptedSecondaryAvailabilityTertiaryDistanceComparator(
      a,
      b,
      requestedInsurance,
      newBooking
    )
  );
